import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './UrlInputModal.module.css';
import commonStyles from '../../styles.module.css';
import UrlInputModalController from './UrlInputModalController';
import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import customElementBoolAttr from '../../utils/customElementBoolAttr';
import WithHint from '../../toolbars/WithHint';

interface UrlInputModalProps {
  fabricObject: fabric.Object;
}

const UrlInputModal: React.FC<UrlInputModalProps> = ApplicationView(
  (props: UrlInputModalProps) => {
    const { fabricObject } = props;

    const controller = useController(UrlInputModalController);
    const inputRef = useRef<HTMLInputElement>(null);

    const [url, setUrl] = useState(fabricObject.linkToUrl);
    const [confirmEnabled, setConfirmEnabled] = useState(
      url && controller.validatedUrl(url) !== null
    );

    // Focus the input when the component mounts
    useEffect(() => {
      const input = inputRef.current;

      if (input) {
        const focusInput = () => {
          input.focus();
          input.select();
        };

        setTimeout(focusInput, 100);
      }
    }, []);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      setUrl(e.target.value);
      if (controller.validatedUrl(e.target.value)) {
        setConfirmEnabled(true);
      } else {
        setConfirmEnabled(false);
      }
    };

    const handleKey = (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
          if (confirmEnabled && url) {
            controller.actionConfirm(url);
            e.preventDefault();
          }
          break;
        case 'Escape':
          controller.actionHideModal();
          e.preventDefault();
          break;
      }
    };

    return (
      <div
        className={classNames(styles.urlInputModalContainer)}
        data-testid='url-input-modal'
      >
        <div
          className={classNames(
            commonStyles.toolbarCommon,
            styles.urlInputModal
          )}
        >
          <aha-button
            kind={'icon'}
            onClick={() => controller.actionHideModal()}
          >
            <aha-icon icon='fa-solid fa-xmark' />
          </aha-button>
          <div className={styles.separator}></div>
          <input
            ref={inputRef}
            className='unstyled-input'
            type='text'
            value={url}
            placeholder='Enter a link to an object or an external URL'
            onChange={handleInput}
            onKeyDown={handleKey}
          />
          {fabricObject.linkToUrl && (
            <WithHint name={'Remove link'}>
              <aha-button
                kind={'icon'}
                onClick={() => controller.actionClear()}
              >
                <aha-icon icon='fa-regular fa-link-simple-slash' />
              </aha-button>
            </WithHint>
          )}
          <aha-button
            disabled={customElementBoolAttr(!confirmEnabled)}
            kind='primary'
            onClick={() => url && controller.actionConfirm(url)}
          >
            Confirm
          </aha-button>
        </div>
      </div>
    );
  }
);

export default StartControllerScope(UrlInputModalController, UrlInputModal);
